import { AfkPingTracker, type ConnectionManager } from '@bn-games/room-client';
import { playAudioSample } from '@bn-games/audio-core';
import type {
  ClientMessage,
  DominoPlayPayload,
  DominoSnapshot,
  ServerMessage,
} from './protocol.js';
import type { Turn } from '../game/types.js';

const RECONNECT_GRACE_MS = 300_000;

/**
 * Canvas-overlay states. The TurnBanner remains the source of truth for whose
 * move it is; this overlay layers on top during connection issues and rematch
 * handshakes — mirror of the checkers/backgammon overlays.
 */
export type OnlineOverlay =
  | 'none'
  | 'opponent_disconnected'
  | 'reconnecting_self'
  | 'game_disconnected'
  | 'rematch_offered'
  | 'rematch_requested'
  | 'rematch_declined'
  | 'game_over';

export type GameOverResult = 'win' | 'loss' | 'draw';

export type OverlayChangeListener = () => void;

/**
 * Optional callbacks the in-game overlay subscribes to so it can react to
 * server-driven resign / restart-consent transitions.
 */
export interface OverlayWiringHandlers {
  readonly onResign?: (winnerIsLocal: boolean) => void;
  readonly onRestartStart?: () => void;
  readonly onRestartStateChange?: () => void;
}

export interface NetworkGameControllerOptions {
  readonly connection: ConnectionManager<ClientMessage, ServerMessage>;
  readonly localSeat: number;
  /** Push a server-authoritative snapshot into the local engine state. */
  readonly onSnapshot: (snapshot: DominoSnapshot) => void;
  readonly onGameOver: (winnerSeats: readonly number[], finalScores: readonly number[]) => void;
  readonly onOverlayChange: OverlayChangeListener;
  readonly onRematchStart: (seat: number, variant: string | null) => void;
}

/**
 * Bridges the room's gameplay protocol to the domino client. Unlike the
 * checkers/backgammon controllers (pure-relay), the domino server is
 * **authoritative** — every move the local player commits is sent as an
 * intent (`DOMINO_PLAY`/`DRAW`/`PASS`/`MUGGINS_CLAIM`/`NEXT_HAND`); the server
 * validates against the engine, then broadcasts the resulting `STATE_SYNC`
 * snapshot back to every seat. The controller funnels those snapshots into
 * `onSnapshot`, which the host wires into the local engine state.
 *
 * Reconnect / opponent-disconnect overlay states mirror the checkers pattern
 * so the rendering layer can reuse the shared `OnlineOverlay` machinery.
 */
export class NetworkGameController {
  private readonly opts: NetworkGameControllerOptions;
  private unsubMessage: (() => void) | null = null;
  private unsubState: (() => void) | null = null;
  private _overlay: OnlineOverlay = 'none';
  private _opponentName = 'Opponent';
  private _gracePeriodEndsAt: number | null = null;
  private _gameOverResult: GameOverResult | null = null;
  private countdownTimer: ReturnType<typeof setInterval> | null = null;
  // ─── Resign / restart-consent ──────────────────────────────────
  private _restartPending = false;
  private _localRestartUsed = false;
  private overlayHandlers: OverlayWiringHandlers = {};
  // ─── AFK ping ──────────────────────────────────────────────────
  private readonly afkPingTracker = new AfkPingTracker();
  // Dedupes notifyTurnState — main.ts calls on every state mutation but the
  // tracker only cares about the turn actually moving. Keyed on *which* seat
  // is on turn, not just "someone else": at 3–4 seats the turn passes between
  // opponents while I keep waiting, and each of those hand-offs restarts the
  // idle clock — otherwise I'd nudge a player who just got the turn.
  private lastTrackerTurnKey: string | null = null;
  private settingsGetter: (() => { afkRemindersEnabled: boolean }) | null = null;

  constructor(opts: NetworkGameControllerOptions) {
    this.opts = opts;
  }

  get overlay(): OnlineOverlay {
    return this._overlay;
  }

  get opponentName(): string {
    return this._opponentName;
  }

  get gracePeriodEndsAt(): number | null {
    return this._gracePeriodEndsAt;
  }

  get gameOverResult(): GameOverResult | null {
    return this._gameOverResult;
  }

  setOpponentName(name: string): void {
    this._opponentName = name;
  }

  showGameOver(result: GameOverResult): void {
    if (this._overlay === 'game_disconnected') return;
    this._gameOverResult = result;
    this.setOverlay('game_over');
  }

  clearGameOver(): void {
    this._gameOverResult = null;
    if (this._overlay === 'game_over' || this._overlay === 'rematch_declined') {
      this.setOverlay('none');
    }
  }

  attach(): void {
    this.unsubMessage = this.opts.connection.onMessage((msg) => this.handleServerMessage(msg));
    this.unsubState = this.opts.connection.onStateChange((state) => {
      if (state === 'disconnected' && this._overlay !== 'game_disconnected') {
        this._gracePeriodEndsAt = Date.now() + RECONNECT_GRACE_MS;
        this.setOverlay('reconnecting_self');
      } else if (state === 'connected' && this._overlay === 'reconnecting_self') {
        this._gracePeriodEndsAt = null;
        this.updateCountdownTicker();
      }
    });
  }

  detach(): void {
    if (this.unsubMessage) {
      this.unsubMessage();
      this.unsubMessage = null;
    }
    if (this.unsubState) {
      this.unsubState();
      this.unsubState = null;
    }
    if (this.countdownTimer !== null) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
    this.afkPingTracker.dispose();
  }

  // ─── AFK ping ─────────────────────────────────────────────────────

  getAfkPingTracker(): AfkPingTracker {
    return this.afkPingTracker;
  }

  setSettingsGetter(getter: () => { afkRemindersEnabled: boolean }): void {
    this.settingsGetter = getter;
  }

  sendAfkPing(): void {
    this.opts.connection.send({ type: 'PING_OPPONENT' });
    this.afkPingTracker.markPingSent();
  }

  /**
   * Called by main.ts whenever the local game's turn ownership may have
   * changed (after applyOnlineSnapshot, rematch start). The dedupe guard
   * means callers don't need to debounce. `activeSeat` is the seat currently
   * on turn — pass it so the idle clock measures how long *that* seat has sat
   * on the move, which is what the nudge is actually about.
   */
  notifyTurnState(isOpponentTurn: boolean, activeSeat: number | null = null): void {
    const key = `${isOpponentTurn}:${activeSeat ?? '-'}`;
    if (this.lastTrackerTurnKey === key) return;
    this.lastTrackerTurnKey = key;
    this.afkPingTracker.setOpponentTurn(isOpponentTurn);
  }

  markGameDisconnected(): void {
    this._gracePeriodEndsAt = null;
    this.setOverlay('game_disconnected');
  }

  // --- Local intents (client → server) ---

  sendPlay(tile: readonly [number, number], armIndex: number, turn: Turn): void {
    const play: DominoPlayPayload = {
      tile: [tile[0], tile[1]] as DominoPlayPayload['tile'],
      armIndex,
      turn,
    };
    this.opts.connection.send({ type: 'DOMINO_PLAY', play });
  }

  sendDraw(): void {
    this.opts.connection.send({ type: 'DOMINO_DRAW' });
  }

  sendPass(): void {
    this.opts.connection.send({ type: 'DOMINO_PASS' });
  }

  sendMugginsClaim(): void {
    this.opts.connection.send({ type: 'DOMINO_MUGGINS_CLAIM' });
  }

  sendNextHand(): void {
    this.opts.connection.send({ type: 'DOMINO_NEXT_HAND' });
  }

  // --- Rematch / overlay actions ---

  requestRematch(): void {
    this.opts.connection.send({ type: 'REMATCH_REQUEST' });
    this.setOverlay('rematch_offered');
  }

  acceptRematch(): void {
    this.opts.connection.send({ type: 'REMATCH_ACCEPT' });
    this.setOverlay('none');
  }

  declineRematch(): void {
    this.opts.connection.send({ type: 'REMATCH_DECLINE' });
    this.setOverlay('none');
  }

  dismissOverlay(): void {
    this.setOverlay('none');
  }

  // ─── Resign / restart-consent (in-game overlay) ────────────────────

  setOverlayHandlers(handlers: OverlayWiringHandlers): void {
    this.overlayHandlers = handlers;
  }

  isRestartPending(): boolean {
    return this._restartPending;
  }

  canLocalProposeRestart(): boolean {
    return !this._localRestartUsed;
  }

  requestResign(): void {
    this.opts.connection.send({ type: 'RESIGN' });
  }

  requestRestart(): void {
    if (this._restartPending || this._localRestartUsed) return;
    this._localRestartUsed = true;
    this.opts.connection.send({ type: 'RESTART_REQUEST' });
    this.overlayHandlers.onRestartStateChange?.();
  }

  acceptRestart(): void {
    this.opts.connection.send({ type: 'RESTART_ACCEPT' });
  }

  declineRestart(): void {
    this.opts.connection.send({ type: 'RESTART_DECLINE' });
  }

  // --- Internals ---

  private setOverlay(overlay: OnlineOverlay): void {
    if (this._overlay === overlay) return;
    this._overlay = overlay;
    this.updateCountdownTicker();
    this.opts.onOverlayChange();
  }

  private updateCountdownTicker(): void {
    const wantsTick =
      this._gracePeriodEndsAt !== null &&
      (this._overlay === 'opponent_disconnected' || this._overlay === 'reconnecting_self');
    if (wantsTick && this.countdownTimer === null) {
      this.countdownTimer = setInterval(() => {
        if (this._gracePeriodEndsAt !== null && Date.now() >= this._gracePeriodEndsAt) {
          this.markGameDisconnected();
          return;
        }
        this.opts.onOverlayChange();
      }, 1000);
    } else if (!wantsTick && this.countdownTimer !== null) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
  }

  private handleServerMessage(msg: ServerMessage): void {
    switch (msg.type) {
      case 'STATE_SYNC':
        this.opts.onSnapshot(msg.snapshot);
        break;

      case 'DOMINO_GAME_OVER':
        this.opts.onGameOver(msg.winnerSeats, msg.finalScores);
        break;

      case 'OPPONENT_DISCONNECTED':
        this._gracePeriodEndsAt = msg.gracePeriodEndsAt;
        this.setOverlay('opponent_disconnected');
        break;

      case 'OPPONENT_RECONNECTED':
        if (this._overlay === 'opponent_disconnected') {
          this._gracePeriodEndsAt = null;
          this.setOverlay('none');
        }
        break;

      case 'RECONNECT_OK':
        this._gracePeriodEndsAt = null;
        if (this._overlay === 'reconnecting_self') this.setOverlay('none');
        break;

      case 'RECONNECT_FAIL':
        this.markGameDisconnected();
        break;

      case 'REMATCH_REQUEST':
        this.setOverlay('rematch_requested');
        break;

      case 'REMATCH_DECLINE':
        this.setOverlay('rematch_declined');
        break;

      case 'REMATCH_START':
        this.setOverlay('none');
        this._restartPending = false;
        this._localRestartUsed = false;
        this.opts.onRematchStart(msg.seat, msg.variant ?? null);
        break;

      case 'RESIGN': {
        // Server is authoritative — trust msg.winner. In 3+ seat FFA / 2v2
        // partners, non-resigning seats are NOT all winners; only the seat
        // server-assigned as winner is. (For N=2 this collapses to "the
        // opponent won iff we didn't resign", which the old heuristic also
        // produced; the fix matters for domino's N≥3 rooms.)
        const winnerIsLocal = msg.winner === this.opts.localSeat;
        this.overlayHandlers.onResign?.(winnerIsLocal);
        break;
      }

      case 'RESTART_PENDING':
        this._restartPending = true;
        this.overlayHandlers.onRestartStateChange?.();
        break;

      case 'RESTART_CANCELLED':
        this._restartPending = false;
        this.overlayHandlers.onRestartStateChange?.();
        break;

      case 'RESTART_REJECTED':
        this._localRestartUsed = false;
        this.overlayHandlers.onRestartStateChange?.();
        break;

      case 'RESTART_START':
        this._restartPending = false;
        this._localRestartUsed = false;
        this.overlayHandlers.onRestartStart?.();
        this.overlayHandlers.onRestartStateChange?.();
        break;

      case 'OPPONENT_PINGED': {
        if (this.settingsGetter?.().afkRemindersEnabled) {
          playAudioSample(`${import.meta.env.BASE_URL}sounds/ping.m4a`);
        }
        break;
      }
    }
  }
}
